import React, { forwardRef } from "react";
import { imgUrl } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { MapPin, Star, ShieldCheck, IndianRupee } from "lucide-react";

/**
 * Instagram / WhatsApp shareable card — 1080×1350 (4:5 portrait).
 * Rendered off-screen, then converted to PNG via html-to-image.
 */
const ShareCard = forwardRef(function ShareCard({ hostel }, ref) {
  const cover = imgUrl(hostel.images?.[0]);
  return (
    <div
      ref={ref}
      style={{
        width: "1080px",
        height: "1350px",
        fontFamily: "'Cabinet Grotesk', 'Satoshi', system-ui, sans-serif",
      }}
      className="relative overflow-hidden bg-[#FBF5EA]"
    >
      {/* Photo half */}
      <div className="relative h-[720px] overflow-hidden">
        {cover ? (
          <img src={cover} crossOrigin="anonymous" alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-slate-200" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/40" />

        {/* Top bar: logo + gender tag */}
        <div className="absolute top-10 left-10 right-10 flex items-start justify-between">
          <div className="flex items-center gap-3 bg-white rounded-2xl px-5 py-3 shadow-lg">
            <LogoMark size={44} />
            <span className="font-black text-3xl">
              Hostel<span className="text-orange-800">Now</span>
            </span>
          </div>
          <span
            className={`text-[18px] tracking-widest uppercase font-black px-5 py-3 rounded-full ${
              hostel.gender === "girls"
                ? "bg-pink-500 text-white"
                : hostel.gender === "boys"
                ? "bg-sky-500 text-white"
                : "bg-emerald-500 text-white"
            }`}
          >
            {hostel.gender}
          </span>
        </div>

        {/* Bottom: price sticker */}
        <div className="absolute bottom-10 left-10 bg-amber-600 text-white rounded-3xl px-8 py-6 shadow-2xl -rotate-3">
          <div className="text-[16px] tracking-[0.3em] uppercase font-bold opacity-90">Starting</div>
          <div className="flex items-baseline font-black text-[96px] leading-none mt-1">
            <IndianRupee size={64} strokeWidth={3.5} />
            {hostel.price_per_bed}
          </div>
          <div className="text-[20px] font-semibold mt-1">/ bed / night</div>
        </div>
      </div>

      {/* Content half */}
      <div className="p-14 pt-12">
        <h1 className="font-black text-[76px] leading-[0.92] tracking-tighter text-slate-900">
          {hostel.name}
        </h1>
        <div className="mt-5 flex items-center gap-4 text-slate-600 text-[26px]">
          <span className="inline-flex items-center gap-2">
            <MapPin size={26} strokeWidth={2.5} className="text-orange-800" /> {hostel.city}
          </span>
          <span className="inline-flex items-center gap-2">
            <Star size={22} strokeWidth={2.5} className="text-amber-600 fill-amber-600" />
            <span className="font-bold text-slate-900">{hostel.rating}</span>
            <span className="text-slate-500">· {hostel.reviews} reviews</span>
          </span>
        </div>

        {hostel.coaching_centers?.length > 0 && (
          <div className="mt-8">
            <div className="text-[16px] tracking-[0.3em] uppercase font-bold text-slate-500">
              Walk to
            </div>
            <div className="mt-3 flex flex-wrap gap-3">
              {hostel.coaching_centers.slice(0, 4).map((c) => (
                <span
                  key={c}
                  className="text-[24px] font-bold px-5 py-2 rounded-full bg-orange-50 text-orange-800 border border-orange-100"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        )}

        {hostel.amenities?.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-3">
            {hostel.amenities.slice(0, 6).map((a) => (
              <span
                key={a}
                className="text-[20px] font-semibold px-4 py-2 rounded-lg bg-slate-100 text-slate-700"
              >
                {a}
              </span>
            ))}
          </div>
        )}

        <div className="mt-10 border-t border-dashed border-slate-300 pt-8 flex items-center justify-between">
          <div>
            <div className="text-[16px] tracking-[0.3em] uppercase font-bold text-slate-500">
              Book instantly
            </div>
            <div className="mt-2 font-black text-[42px] tracking-tighter">HostelNow.com</div>
          </div>
          <div className="text-right">
            <div className="inline-flex items-center gap-2 bg-stone-900 text-white rounded-full px-5 py-3">
              <ShieldCheck size={22} strokeWidth={2.5} />
              <span className="font-bold text-[20px]">Aadhaar KYC verified</span>
            </div>
            <div className="mt-3 text-[18px] text-slate-500 font-medium">
              {hostel.available_beds}/{hostel.total_beds} beds available
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ShareCard;
